<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      :show.sync="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <add-canned-modal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Translate Content -->
    <translate-modal
      v-if="showTranslateModal"
      :content="messageContent"
      :content-attributes="contentAttributes"
      @close="onCloseTranslateModal"
    />
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal"
      class="context-menu--delete-modal"
      :show.sync="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <woot-button
      icon="more-vertical"
      color-scheme="secondary"
      variant="clear"
      size="small"
      @click="handleOpen"
    />
    <woot-context-menu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <div v-if="hasCampaignName" class="campaign-name-item">
          <fluent-icon icon="megaphone" size="14" class="campaign-icon" />
          <span class="campaign-label">Campaign:</span>
          <span class="campaign-value">{{ campaignName }}</span>
        </div>
        <hr v-if="hasCampaignName" />
        <menu-item
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: this.$t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click="handleCopy"
        />
        <menu-item
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'translate',
            label: this.$t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click="handleTranslate"
        />
        <hr />
        <menu-item
          :option="{
            icon: 'link',
            label: this.$t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click="copyLinkToMessage"
        />
        <menu-item
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: this.$t(
              'CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'
            ),
          }"
          variant="icon"
          @click="showCannedResponseModal"
        />
        <menu-item
          v-if="isCallHistoryOrRecordingMessage"
          :option="{
            icon: 'arrow-sync',
            label: this.$t('CONVERSATION.CONTEXT_MENU.SYNC_RECORDING'),
          }"
          variant="icon"
          @click="handleSyncRecording"
        />
        <menu-item
          v-if="isDomesticCallDetailsMessage"
          :option="{
            icon: 'document-text',
            label: this.$t('CONVERSATION.CONTEXT_MENU.SYNC_SUMMARY'),
          }"
          variant="icon"
          @click="handleSyncSummary"
        />
        <hr v-if="enabledOptions['delete'] && !message.private" />
        <menu-item
          v-if="enabledOptions['delete'] && !message.private"
          :option="{
            icon: 'delete',
            label: this.$t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click="openDeleteModal"
        />
      </div>
    </woot-context-menu>
  </div>
</template>
<script>
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import { ACCOUNT_EVENTS } from '../../../helper/AnalyticsHelper/events';
import TranslateModal from 'dashboard/components/widgets/conversation/bubble/TranslateModal';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';

export default {
  components: {
    AddCannedModal,
    TranslateModal,
    MenuItem,
  },
  mixins: [alertMixin, clickaway, messageFormatterMixin],
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    campaignName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showTranslateModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return this.message.content_attributes;
    },
    isCallHistoryOrRecordingMessage() {
      // Check if message content contains call history or recording indicators
      const content = this.messageContent || '';
      return content.includes('**Call Initiated:**');

    },
    isDomesticCallDetailsMessage() {
      // Check if message content contains "Domestic Call Details"
      const content = this.messageContent || '';
      return content.includes('**Domestic Call Details:**')||
             (this.contentAttributes && (
               this.contentAttributes.call_id ||
               this.contentAttributes.recording_url ||
               this.contentAttributes.audio_url
             ));
    },
    hasCampaignName() {
      return !!this.campaignName;
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      this.showAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      this.showAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      this.$track(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale } = this.getAccount(this.currentAccountId);
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage: locale || 'en',
      });
      this.handleClose();
      this.showTranslateModal = true;
    },
    onCloseTranslateModal() {
      this.showTranslateModal = false;
    },

    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        this.showAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    async handleSyncRecording() {
      try {
        const response = await this.$store.dispatch('syncRecording', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        console.log("Response: ", response);
        const message = response?.data?.message || this.$t('CONVERSATION.CONTEXT_MENU.SYNC_RECORDING_INITIATED');
        this.showAlert(message);
      } catch (error) {
        console.log('Error syncing recording:', error);
        const errorMessage = error?.response?.data?.message || error?.message || this.$t('CONVERSATION.CONTEXT_MENU.SYNC_RECORDING_FAILED');
        this.showAlert(errorMessage);
      }finally{
        this.handleClose();
      }
    },
    async handleSyncSummary() {
      try {
        const response = await this.$store.dispatch('syncSummary', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        console.log("Summary Response: ", response);
        const message = response?.message || this.$t('CONVERSATION.CONTEXT_MENU.SYNC_SUMMARY_INITIATED');
        this.showAlert(message);
      } catch (error) {
        console.log('Error syncing summary:', error);
        const errorMessage = error?.response?.data?.message || error?.message || this.$t('CONVERSATION.CONTEXT_MENU.SYNC_SUMMARY_FAILED');
        this.showAlert(errorMessage);
      } finally {
        this.handleClose();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-white dark:bg-slate-900 shadow-xl rounded-md;

  .campaign-name-item {
    @apply px-3 py-2 text-sm flex items-center gap-2;
    
    .campaign-icon {
      color: #9333ea;
    }
    
    .campaign-label {
      @apply font-semibold text-slate-700 dark:text-slate-300;
    }
    
    .campaign-value {
      color: #9333ea;
      font-weight: 500;
    }
  }

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-slate-50 dark:border-slate-800/50;
  }
}

.context-menu--delete-modal {
  :deep {
    .modal-container {
      @apply max-w-[30rem];

      h2 {
        @apply font-medium text-base;
      }
    }

    .modal-footer {
      @apply pt-4 pb-8 px-8;
    }
  }
}
</style>
